import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "helpers/cn";

// print:hidden — a tooltip left open by the cursor must not land in the printout.
const tooltipContentVariants = cva("z-tooltip w-fit max-w-xs text-balance print:hidden", {
  variants: {
    variant: {
      default:
        "rounded-md bg-primary px-3 py-1.5 text-caption text-primary-foreground shadow-md",
      card: "rounded-lg bg-card px-3 py-2.5 text-card-foreground shadow-elevated"
    }
  },
  defaultVariants: { variant: "default" }
});

// The arrow is a filled shape, so it needs the content's own background.
const tooltipArrowVariants = cva("", {
  variants: { variant: { default: "fill-primary", card: "fill-card" } },
  defaultVariants: { variant: "default" }
});

// Dense grids (year headers, calendar cells) would otherwise flash a card for
// every icon the cursor crosses on its way somewhere else.
export const HOVER_DELAY_MS = 200;

const TooltipProvider = ({
  delayDuration = HOVER_DELAY_MS,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    data-slot="tooltip-provider"
    delayDuration={delayDuration}
    {...props}
  />
);

// Self-wraps a Provider so a bare <Tooltip> works inside any of the app's
// independent React roots without a shared top-level provider.
const Tooltip = (props: ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipProvider>
    <TooltipPrimitive.Root data-slot="tooltip" {...props} />
  </TooltipProvider>
);

const TooltipTrigger = (props: ComponentProps<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
);

const TooltipContent = ({
  className,
  sideOffset = 4,
  variant,
  arrow = true,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content> &
  VariantProps<typeof tooltipContentVariants> & { arrow?: boolean }) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      className={cn(tooltipContentVariants({ variant }), className)}
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      {...props}
    >
      {children}
      {arrow && <TooltipPrimitive.Arrow className={tooltipArrowVariants({ variant })} />}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
);

export {
  Tooltip,
  TooltipContent,
  tooltipContentVariants,
  TooltipProvider,
  TooltipTrigger
};
